import { attachmentFileExtension } from "@cleo/contracts";

// HEIC/HEIF photos (UP8, upstream #8161 as a reference): providers accept
// JPEG, browsers hand HEIC as `image/heic`, an empty type or
// `application/octet-stream` with a `.heic` name. A photo converts to JPEG on
// the device before it enters the image lane. Conversion uses the platform's
// own decoder when it has one (Safari and the macOS WebView decode HEIC); a
// platform without one answers `undecodable` and the composer says so — it
// never attaches bytes no provider can read.

const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);
const HEIC_EXTENSIONS = new Set(["heic", "heif", "hif"]);
/** Photos beyond this many pixels are refused rather than decoded into a bitmap that can exhaust the tab. */
export const MAX_HEIC_PIXELS = 48_000_000;
const JPEG_QUALITY = 0.9;

export function isHeicFile(file: { readonly name: string; readonly type: string }): boolean {
  const type = file.type.trim().toLowerCase();
  if (HEIC_MIME_TYPES.has(type)) return true;
  // A concrete image type wins over a misleading name (a PNG called photo.heic is a PNG).
  if (type.startsWith("image/")) return false;
  return HEIC_EXTENSIONS.has(attachmentFileExtension(file.name));
}

export function jpegNameFor(name: string): string {
  const extension = attachmentFileExtension(name);
  const stem = extension.length > 0 ? name.slice(0, name.length - extension.length - 1) : name;
  return `${stem.length > 0 ? stem : "photo"}.jpg`;
}

export type HeicConversionResult =
  | { readonly ok: true; readonly file: File }
  | { readonly ok: false; readonly reason: "undecodable" | "too-large"; readonly detail: string };

export interface HeicDecoder {
  /** Decode a HEIC file into a bitmap. The platform's `createImageBitmap` is the default. */
  readonly decode: (file: File) => Promise<ImageBitmap>;
}

const platformDecoder: HeicDecoder = {
  decode: (file) => createImageBitmap(file),
};

export async function convertHeicToJpeg(
  file: File,
  decoder: HeicDecoder = platformDecoder,
): Promise<HeicConversionResult> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await decoder.decode(file);
  } catch {
    return {
      ok: false,
      reason: "undecodable",
      detail: `'${file.name}' is a HEIC photo this device cannot decode. Export it as JPEG and attach that.`,
    };
  }
  try {
    if (bitmap.width * bitmap.height > MAX_HEIC_PIXELS) {
      return {
        ok: false,
        reason: "too-large",
        detail: `'${file.name}' is ${bitmap.width}×${bitmap.height}; photos over ${MAX_HEIC_PIXELS / 1_000_000} MP are not converted here.`,
      };
    }
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) {
      return { ok: false, reason: "undecodable", detail: "The image canvas is unavailable." };
    }
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob) {
      return {
        ok: false,
        reason: "undecodable",
        detail: `'${file.name}' could not be encoded as JPEG.`,
      };
    }
    return {
      ok: true,
      file: new File([blob], jpegNameFor(file.name), {
        type: "image/jpeg",
        lastModified: file.lastModified,
      }),
    };
  } finally {
    bitmap.close();
  }
}
